import { useTranslation } from 'react-i18next';
import { TableColumn } from 'react-data-table-component';
import dayjs from 'dayjs';

import { Breakpoints } from 'hooks/Screen/useScreenSize';
import { Encounter } from 'api/encounters/types';
import EncStatusCell from 'components/ObjectEncs/EncStatusCell';
import { PestOrTracesCell } from 'components/ObjectEncs/PestOrTraces';
import UserDataCell from 'components/ObjectVuls/UserDataCell';
import { InspectionDateTimeCell } from 'components/ObjectEncs/InspectionDateTimeCell';
import SignLinkCell from 'components/TrapsTables/Cells/LinkCell/SignLinkCell';
import { SignType } from 'api/InteractiveMap/types';

export const useGetObjectEncColumns = (slug: string): TableColumn<Encounter>[] => {
  const { t } = useTranslation();
  return [
    {
      name: t('ObjectVuls.table.status'),
      selector: row => row.status,
      sortable: true,
      sortField: 'status',
      cell: row => <EncStatusCell status={row.status} />,
      width: '105px',
    },
    {
      name: t('ObjectVuls.table.map'),
      selector: row => row.floor_plan?.slug || '',
      cell: row => (
        <SignLinkCell
          data={row.floor_plan ?? null}
          slug={slug}
          enc_id={row.id}
          icon="ri-treasure-map-line"
          sign_type={SignType.sightings}
        />
      ),
      grow: 2,
      hide: Breakpoints.Mobile,
    },
    {
      name: t('ObjectEncs.discoverd_at'),
      selector: row => row.discovery_datetime,
      sortable: true,
      sortField: 'discovery_datetime',
      cell: row => `${dayjs(row.discovery_datetime).format('DD.MM.YYYY HH:mm')}`,
      grow: 2,
    },
    {
      name: t('ObjectEncs.location'),
      selector: row => row.place,
      hide: Breakpoints.Mobile,
    },
    {
      name: t('ObjectEncs.pest'),
      selector: row => row.pest?.name_US || '',
      sortable: true,
      sortField: 'pest',
      cell: row => <PestOrTracesCell value={row.pest?.name_US || ''} />,
      hide: Breakpoints.Tablet,
    },
    {
      name: t('ObjectEncs.amount'),
      selector: row => row.amount || '',
      sortable: true,
      sortField: 'amount',
      cell: row => <PestOrTracesCell value={row.amount} />,
      hide: Breakpoints.Tablet,
    },
    {
      name: t('ObjectEncs.traces'),
      selector: row => row.pest_traces?.name_US || '',
      sortable: true,
      sortField: 'pest_traces',
      cell: row => <PestOrTracesCell value={row.pest_traces?.name_US || ''} />,
      hide: Breakpoints.Tablet,
    },
    {
      name: t('ObjectEncs.witnesses'),
      selector: row => row.witness.name,
      hide: Breakpoints.LargeDesktop,
    },
    {
      name: t('ObjectVuls.table.instpector'),
      selector: row => row.responsible_person.name,
      cell: row => <UserDataCell user={row.responsible_person} />,
      grow: 2,
      hide: Breakpoints.ExtraLarge,
    },
    {
      name: t('ObjectEncs.inspected_at'),
      selector: row => row.fix_datetime || '',
      sortable: true,
      sortField: 'fix_datetime',
      cell: row => <InspectionDateTimeCell datetime={row.fix_datetime} />,
      hide: Breakpoints.ExtraLarge,
    },
    {
      name: t('ObjectEncs.actions'),
      selector: row => row.fix_comment || '',
      cell: row => row.fix_comment || <h6 className="text-danger text-center">-</h6>,
      hide: Breakpoints.ExtraLarge,
    },
    {
      name: t('ObjectEncs.associated_pccs'),
      selector: row => row.connected_vuls?.length || 0,
      cell: row => <h4>{row.connected_vuls?.length || 0}</h4>,
      hide: Breakpoints.ExtraLarge,
    },
  ];
};
